import React, { useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { TspBreadcrumb } from "../../components/primitives";
import TspLoadError from "../../components/common/TspLoadError";
import useTspApiResource from "../../hooks/useTspApiResource";
import TspApiService from "../../api/tspApiService";
import { JOURNEY_RAIL_SECTIONS } from "../../constants/tspJourneyRail";
import useTspJourneyWorkflowActions from "../journeys/useTspJourneyWorkflowActions";
import JourneySectionNav from "../journeys/components/JourneySectionNav";
import useTspStatsData from "./useTspStatsData";
import DistributionSummarySection from "./components/DistributionSummarySection";
import PublicResponseSummarySection from "./components/PublicResponseSummarySection";
import LogisticsSummarySection from "./components/LogisticsSummarySection";
import MediaGallerySection from "./components/MediaGallerySection";
import TspTempleStatsView from "./TspTempleStatsView";

const NOTHING = () => Promise.resolve({ data: null });

// /tsp/stats — "Your Stats" from the menu: overall aggregate summary of all
// journeys created for the user's temple(s) (TspTempleStatsView).
// /tsp/stats/:journeyId — one journey's stats, opened from that journey (its
// Page Sections rail).
export default function TspStatsPage() {
  const { journeyId } = useParams();
  return journeyId ? <TspJourneyStatsView /> : <TspTempleStatsView />;
}

// One journey's summary view (distribution, public response, logistics, media).
// Shares the Page Sections / Quick Actions rail with the Update Journey screen
// (GET /api/tsp/stats?journey_id=).
function TspJourneyStatsView() {
  const { journeyId: routeJourneyId } = useParams();
  const { hash } = useLocation();
  const { data, loading, error, reload: reloadStats } = useTspStatsData(routeJourneyId);
  const resolvedJourneyId = data?.journey?.id;

  // The full journey (capabilities/allowed_actions) for the shared Quick
  // Actions rail — fetched once the summary above resolves which journey it is.
  const journey = useTspApiResource(() => (resolvedJourneyId ? TspApiService.getJourney(resolvedJourneyId) : NOTHING()), [resolvedJourneyId]);
  const { quickActions, handleAction, dialogs } = useTspJourneyWorkflowActions(journey.data, () => {
    reloadStats();
    journey.reload();
  });

  // Cross-page rail links arrive as /tsp/stats#<section>; scroll once the
  // (async-loaded) sections exist.
  useEffect(() => {
    if (!data || !hash) return;
    document.getElementById(hash.slice(1))?.scrollIntoView();
  }, [data, hash]);

  if (error) return <TspLoadError error={error} onRetry={reloadStats} what="stats" />;
  if (loading) return <div className="tsp-page tsp-dashboard-loading">Loading stats…</div>;

  if (!data?.journey) {
    return (
      <div className="tsp-page tsp-stats-page">
        <p className="tsp-muted-text">No journey to show stats for yet. Create or join a journey to see its stats here.</p>
      </div>
    );
  }

  const { journey: journeySummary, cities, distribution_summary, public_response, logistics, media_gallery } = data;
  const pageRoutes = { journey: `/tsp/journeys/${journeySummary.id}`, stats: `/tsp/stats/${journeySummary.id}` };

  return (
    <div className="tsp-page tsp-stats-page">
      <div className="tsp-journey-detail-layout">
        <div className="tsp-journey-detail-main">
          <TspBreadcrumb items={["Dashboard", "Journeys", journeySummary.name]} />
          <span id="overview" className="tsp-anchor" />
          <div id="distribution-summary">
            <DistributionSummarySection summary={distribution_summary} />
          </div>
          <div id="public-response">
            <PublicResponseSummarySection response={public_response} cities={cities} />
          </div>
          <div id="logistics">
            <LogisticsSummarySection logistics={logistics} />
          </div>
          <div id="media-gallery">
            <MediaGallerySection gallery={media_gallery} />
          </div>
        </div>

        <JourneySectionNav sections={JOURNEY_RAIL_SECTIONS} quickActions={quickActions} currentPage="stats" pageRoutes={pageRoutes} onAction={handleAction} />
      </div>

      {dialogs}
      <ToastContainer />
    </div>
  );
}
